import { useState } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Divider,
  Checkbox,
  Avatar,
  IconButton,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DialogContainer from "../../../../../../../components/dialog-container/dialog-container";
import TextInput from "../../../../../../../components/textinput/textinput";
import { useFormik } from "formik";
import * as yup from "yup";
import { websiteRegex } from "../../../../../../../utils/Constant";
import { updateClientDivisionDetailsRequest } from "../../../../../../../utils/APICalls";
import { useDispatch } from "react-redux";
import { getClientSubDivisionListRequest, getSubdivisionDetailsRequest } from "../../../../../../../redux/clients/slice";
import ErrorNotification from "../../../../../../../components/error-notification/error-notification";
import Loading from "../../../../../../../components/loading/loading";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data: any;
  clientId: string;
  onAddDivision: any;
};

const validationSchema = yup.object({
  divisionName: yup.string().required("Division name is required"),
  website: yup.string().matches(websiteRegex, "Invalid Website"),
  useParentWebsite: yup.boolean(),
  useParentLogo: yup.boolean(),
});

export default function EditDivisionDetails({
  showDialog,
  closeDialog,
  data,
  clientId,
  onAddDivision,
}: props) {
  const [selectedFile, setSelectedFile] = useState<any>();
  const [selectedFileSrc, setSelectedFileSrc] = useState<any>();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      divisionName: data?.displayName || "",
      website: data?.website || "",
      logo: null,
      useParentWebsite: data?.useParentWebsite || false,
      useParentLogo: data?.useParentLogo || false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (selectedFile && selectedFile.size > 5000000) {
        formik.setFieldError("logo", "File size exceeds 5MB limit.");
        return;
      }
      let finalWebsite = null;
      if (values.website.length > 0) {
        finalWebsite = values.website.includes("http")
          ? values.website
          : `https://${values.website}`;
      }
      const formData = new FormData();
      formData.append("displayName", values?.divisionName || "");
      formData.append("useParentWebsite", String(values?.useParentWebsite));
      if (!values?.useParentWebsite) {
        formData.append("website", finalWebsite || "");
      }
      formData.append("useParentLogo", String(values?.useParentLogo));
      if (!values?.useParentLogo && values?.logo) {
        formData.append("logo", values.logo);
      }
      updateDivisionDetails(formData);
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileUploaded = event?.target?.files?.[0];

    if (fileUploaded) {
      setSelectedFile(fileUploaded);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFileSrc(reader.result);
      };
      reader.readAsDataURL(fileUploaded);
      if (fileUploaded.size > 5000000) {
        formik.setFieldError("logo", "File size exceeds 5MB limit.");
      } else {
        formik.setFieldValue("logo", fileUploaded);
      }
    } else {
      setSelectedFile(null);
    }
  };

  const handleFileDelete = () => {
    setSelectedFile(null);
    formik.setFieldValue("logo", null);
  };

  const updateDivisionDetails = async (payload: any) => {
    setShowLoading(true);
    const response = await updateClientDivisionDetailsRequest(
      clientId,
      data?.id,
      payload
    );
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(
        getSubdivisionDetailsRequest({ id: clientId, subDivisionId: data?.id })
      );
      dispatch(getClientSubDivisionListRequest({ id: clientId }));
      onAddDivision({id: data?.id});
      closeDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <DialogContainer
      title="Edit Client Division - Division Details"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">Division Name</Typography>
          <TextInput
            className="field-input"
            id="divisionName"
            name="divisionName"
            label="Division Name"
            variant="outlined"
            value={formik.values.divisionName}
            onChange={formik.handleChange}
            error={
              formik.touched.divisionName && Boolean(formik.errors.divisionName)
            }
            helperText={
              formik.touched.divisionName &&
              (formik.errors.divisionName as string)
            }
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Website</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={formik.handleChange}
                  checked={formik.values.useParentWebsite}
                  name="useParentWebsite"
                />
              }
              label="Same as Primary"
            />
            <TextInput
              sx={{ width: "100% !important", mt: 1 }}
              className="field-input"
              id="website"
              label="Website"
              variant="outlined"
              value={formik.values.website}
              onChange={formik.handleChange}
              error={formik.touched.website && Boolean(formik.errors.website)}
              helperText={
                formik.touched.website && (formik.errors.website as string)
              }
              disabled={formik.values.useParentWebsite}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Logo</Typography>
          <Box sx={{ width: "calc(100% - 200px)" }}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={formik.handleChange}
                  name="useParentLogo"
                  checked={formik.values.useParentLogo}
                />
              }
              label="Same as Primary"
            />
            <Box
              sx={{
                height: 80,
                width: "100%",
                border: "1px dashed rgba(0, 0, 0, 0.12)",
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <UploadFileIcon
                sx={{ ml: 5 }}
                color={formik.values.useParentLogo ? "disabled" : "primary"}
              />
              <Box ml={2}>
                <Box sx={{ display: "flex" }}>
                  <Typography
                    color={formik.values.useParentLogo ? "disabled" : "primary"}
                    sx={{ textDecoration: "underline" }}
                  >
                    Click to upload
                  </Typography>
                  <Typography sx={{ ml: 1 }}>or drag and drop</Typography>
                </Box>
                <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                  PNG or JPG (max. 5MB)
                </Typography>
              </Box>
              <input
                style={{
                  display: "block",
                  height: 80,
                  width: "100%",
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  opacity: 0,
                  cursor: "pointer",
                }}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                name="logo"
                onClick={(event) => {
                  // same file selection issue
                  (event.target as HTMLInputElement).value = "";
                }}
                disabled={formik.values.useParentLogo}
              />
            </Box>
            {!formik.values.useParentLogo && selectedFile && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1.5px solid green",
                  p: 0.8,
                  borderRadius: 1,
                  backgroundColor: "#F7FAF7",
                }}
              >
                <Box display={"flex"} alignItems={"center"}>
                  <Avatar
                    sx={{ ml: 0.5 }}
                    className="division-logo"
                    alt={selectedFile?.name}
                    variant="circular"
                    src={selectedFileSrc}
                  />
                  <Box sx={{ ml: 1.5 }}>
                    <Typography>
                      {selectedFile?.name || "No file selected"}
                    </Typography>
                    <Typography>
                      {" "}
                      {selectedFile
                        ? `${(selectedFile.size / 1024).toFixed(2)} kb`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <IconButton aria-label="delete" onClick={handleFileDelete}>
                    <DeleteOutlinedIcon color="primary" />
                  </IconButton>
                </Box>
              </Box>
            )}
            {Boolean(formik.errors.logo) && (
              <Box sx={{ color: "red", fontSize: 12, mt: 0.5 }}>
                {formik.errors.logo as string}
              </Box>
            )}
          </Box>
        </Box>
      </form>
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      {showLoading && <Loading />}
    </DialogContainer>
  );
}
